/**
 * AppearanceSection — Collapsible appearance controls for the page editor sidebar.
 *
 * Extracted from SectionConfigPanel to keep file sizes manageable.
 * Wraps responsive-eligible properties in ResponsivePropertyGroup.
 */

import { useCallback } from 'react'
import { ChevronDown, X } from 'lucide-react'
import type { ResponsiveOverrides } from '@zaviago/storefront-config'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@/components/ui/collapsible'
import type { EditorSection } from './usePageSections'
import { ColorControl, PresetControl } from './controls'
import { ResponsivePropertyGroup } from './controls/ResponsivePropertyGroup'

// ---------------------------------------------------------------------------
// Preset definitions
// ---------------------------------------------------------------------------

const PADDING_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

const RADIUS_PRESETS = [
  { value: 'none', label: 'None' },
  { value: 'sm', label: 'Small' },
  { value: 'md', label: 'Medium' },
  { value: 'lg', label: 'Large' },
]

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppearanceSectionProps {
  section: EditorSection
  onAppearanceChange: (key: string, value: unknown) => void
  onResponsiveChange: (breakpoint: 'tablet' | 'mobile', key: string, value: unknown) => void
  onClearResponsive: (breakpoint: 'tablet' | 'mobile', key: string) => void
  /** Active breakpoint from DeviceToggle — highlights the matching row */
  activeBreakpoint?: 'desktop' | 'tablet' | 'mobile'
  /** Fires when user edits a breakpoint row — syncs DeviceToggle */
  onBreakpointEdit?: (breakpoint: 'desktop' | 'tablet' | 'mobile') => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AppearanceSection({
  section,
  onAppearanceChange,
  onResponsiveChange,
  onClearResponsive,
  activeBreakpoint,
  onBreakpointEdit,
}: AppearanceSectionProps) {
  const responsive = section.appearance?.responsive as ResponsiveOverrides | undefined

  // Curry handlers for ResponsivePropertyGroup
  const makeResponsiveHandler = useCallback(
    (key: string) => (bp: 'tablet' | 'mobile', value: unknown) => {
      onResponsiveChange(bp, key, value)
    },
    [onResponsiveChange],
  )

  const makeClearHandler = useCallback(
    (key: string) => (bp: 'tablet' | 'mobile') => {
      onClearResponsive(bp, key)
    },
    [onClearResponsive],
  )

  return (
    <Collapsible>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2">
        <h3 className="text-[13px] font-semibold uppercase tracking-wider text-gray-400">
          Appearance
        </h3>
        <ChevronDown className="h-4 w-4 text-gray-400 transition-transform [[data-state=open]_&]:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-4 pt-2">
          {/* Background Color — responsive */}
          <ResponsivePropertyGroup
            label="Background Color"
            propertyKey="bg"
            desktopValue={(section.props._bg as string) ?? undefined}
            responsive={responsive}
            onDesktopChange={(v) => onAppearanceChange('_bg', v)}
            onResponsiveChange={makeResponsiveHandler('bg')}
            onClearResponsive={makeClearHandler('bg')}
            activeBreakpoint={activeBreakpoint}
            onBreakpointEdit={onBreakpointEdit}
          >
            {(value, onChange) => (
              <ColorControl
                value={value as string | undefined}
                onChange={(v) => onChange(v)}
                label=""
              />
            )}
          </ResponsivePropertyGroup>

          {/* Text Color — NOT responsive (not in ResponsiveAppearanceProps) */}
          <ColorControl
            value={(section.props._textColor as string) ?? undefined}
            onChange={(v) => onAppearanceChange('_textColor', v)}
            label="Text Color"
          />

          {/* Padding — responsive */}
          <ResponsivePropertyGroup
            label="Padding"
            propertyKey="padding"
            desktopValue={(section.props._padding as string) ?? 'none'}
            responsive={responsive}
            onDesktopChange={(v) => onAppearanceChange('_padding', v)}
            onResponsiveChange={makeResponsiveHandler('padding')}
            onClearResponsive={makeClearHandler('padding')}
            activeBreakpoint={activeBreakpoint}
            onBreakpointEdit={onBreakpointEdit}
          >
            {(value, onChange) => (
              <PresetControl
                value={(value as string) ?? 'none'}
                onChange={(v) => onChange(v)}
                label=""
                options={PADDING_PRESETS}
              />
            )}
          </ResponsivePropertyGroup>

          {/* Corner Radius — responsive */}
          <ResponsivePropertyGroup
            label="Corner Radius"
            propertyKey="radius"
            desktopValue={(section.props._radius as string) ?? 'none'}
            responsive={responsive}
            onDesktopChange={(v) => onAppearanceChange('_radius', v)}
            onResponsiveChange={makeResponsiveHandler('radius')}
            onClearResponsive={makeClearHandler('radius')}
            activeBreakpoint={activeBreakpoint}
            onBreakpointEdit={onBreakpointEdit}
          >
            {(value, onChange) => (
              <PresetControl
                value={(value as string) ?? 'none'}
                onChange={(v) => onChange(v)}
                label=""
                options={RADIUS_PRESETS}
              />
            )}
          </ResponsivePropertyGroup>

          {/* Max Width — responsive */}
          <ResponsivePropertyGroup
            label="Max Width"
            propertyKey="maxWidth"
            desktopValue={section.props._maxWidth as number | undefined}
            responsive={responsive}
            onDesktopChange={(v) => onAppearanceChange('_maxWidth', v)}
            onResponsiveChange={makeResponsiveHandler('maxWidth')}
            onClearResponsive={makeClearHandler('maxWidth')}
            activeBreakpoint={activeBreakpoint}
            onBreakpointEdit={onBreakpointEdit}
          >
            {(value, onChange) => (
              <MaxWidthControl
                value={value as number | undefined}
                onChange={(v) => onChange(v)}
              />
            )}
          </ResponsivePropertyGroup>
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ---------------------------------------------------------------------------
// MaxWidthControl — inline, avoids a separate file for 1 usage
// ---------------------------------------------------------------------------

function MaxWidthControl({
  value,
  onChange,
}: {
  value: number | undefined
  onChange: (v: number | undefined) => void
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        {value != null && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="ml-auto rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            title="Reset to page default"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={320}
          max={1920}
          step={10}
          value={value ?? 1920}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10)
            onChange(v >= 1920 ? undefined : v)
          }}
          className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-gray-200 accent-sky-500"
        />
        <span className="w-14 text-right text-xs tabular-nums text-gray-500">
          {value != null ? `${value}px` : 'Default'}
        </span>
      </div>
    </div>
  )
}
